/**
 * Value, time and size formatters. The single home for display formatting so a
 * voltage reads identically on every screen. All numeric output is intended to
 * render in IBM Plex Mono (apply `font-mono` at the call site).
 */

const DASH = '—'

/**
 * Format a tag/metric value for display, optionally with a unit suffix.
 * Numbers are fixed to `precision` decimals; null/undefined render as an em dash.
 */
export function formatValue(
  value: number | string | boolean | null | undefined,
  unit?: string,
  precision = 2,
): string {
  if (value === null || value === undefined) return DASH
  if (typeof value === 'boolean') return value ? 'ON' : 'OFF'
  const text = typeof value === 'number' ? formatNumber(value, precision) : value.trim() || DASH
  return unit ? `${text} ${unit}` : text
}

/** Format a number with grouping and fixed precision. */
export function formatNumber(value: number, precision = 2): string {
  if (!Number.isFinite(value)) return DASH
  return value.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  })
}

/** Format an ISO timestamp as a locale date-time string. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return DASH
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return DASH
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

/** Short relative time, e.g. "3m ago", "2h ago", "just now". */
export function formatRelativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return DASH
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return DASH
  const sec = Math.round((now - then) / 1000)
  if (sec < 5) return 'just now'
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const days = Math.floor(hr / 24)
  return `${days}d ago`
}

/**
 * Format a duration in milliseconds as HH:MM:SS (clamped at 0).
 * Used for alarm durations and gateway uptimes.
 */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

/** Human-readable byte size, e.g. "1.5 MB". */
export function formatBytes(bytes: number, precision = 1): string {
  if (!Number.isFinite(bytes) || bytes < 0) return DASH
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, exp)
  return `${value.toFixed(exp === 0 ? 0 : precision)} ${units[exp]}`
}

/** Format a 0–1 ratio (or 0–100 number) as a percentage string. */
export function formatPercent(value: number | null | undefined, precision = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH
  const pct = value <= 1 && value >= -1 ? value * 100 : value
  return `${pct.toFixed(precision)}%`
}
